import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from 'components/Assessments/adapters/types'
import { SectionAdapter } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SupervisoryReviewFormField } from './formField'
import { RecertFormData } from '../../types/RecertFormData'
import { RecertAssessmentQuestionnaireItem } from '../../types/questionnaire'

export class SupervisoryReviewAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const idBadge = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE
    ).at(0)

    const honoringRights = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS
    ).at(0)

    const emergencyProtocol = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL
    ).at(0)

    const hygienePrecautions = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS
    ).at(0)

    const reportingChanges = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES
    ).at(0)

    const grievanceComplaint = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT
    ).at(0)

    const pocDocReviewedWithCaregiver = getAnswerValuesFromItems(
      [
        flatAnswers[
          RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
        ]
      ],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
    ).at(0)

    const ppeAccess = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS
    ).at(0)

    const supervisionObserved = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED
    ).at(0)

    const taskObserved = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED
    ).at(0)

    const patientStatus = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS
    ).at(0)

    const comments = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS
    ).at(0)

    const caregiverEducationTopic = getAnswerValuesFromItems(
      [
        flatAnswers[
          RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC
        ]
      ],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC
    ).at(0)

    const educationProvided = getAnswerValuesFromItems(
      [
        flatAnswers[
          RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED
        ]
      ],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED
    ).at(0)

    const initialsAuthorization = getAnswerValuesFromItems(
      [flatAnswers[RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_INITIALS_AUTHORIZATION]],
      RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_INITIALS_AUTHORIZATION
    ).at(0)

    return {
      [SupervisoryReviewFormField.ID_BADGE]: idBadge,
      [SupervisoryReviewFormField.HONORING_RIGHTS]: honoringRights,
      [SupervisoryReviewFormField.EMERGENCY_PROTOCOL]: emergencyProtocol,
      [SupervisoryReviewFormField.HYGIENE_PRECAUTIONS]: hygienePrecautions,
      [SupervisoryReviewFormField.REPORTING_CHANGES]: reportingChanges,
      [SupervisoryReviewFormField.GRIEVANCE_COMPLAINT]: grievanceComplaint,
      [SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]: pocDocReviewedWithCaregiver,
      [SupervisoryReviewFormField.PPE_ACCESS]: ppeAccess,
      [SupervisoryReviewFormField.SUPERVISION_OBSERVED]: supervisionObserved,
      [SupervisoryReviewFormField.TASK_OBSERVED]: taskObserved,
      [SupervisoryReviewFormField.PATIENT_STATUS]: patientStatus,
      [SupervisoryReviewFormField.COMMENTS]: comments,
      [SupervisoryReviewFormField.CAREGIVER_EDUCATION_TOPIC]: caregiverEducationTopic,
      [SupervisoryReviewFormField.EDUCATION_PROVIDED]: educationProvided,
      [SupervisoryReviewFormField.INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(formData: RecertFormData): Array<{ linkId: string; value: unknown }> {
    return [
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE,
        value: formData[SupervisoryReviewFormField.ID_BADGE]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS,
        value: formData[SupervisoryReviewFormField.HONORING_RIGHTS]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL,
        value: formData[SupervisoryReviewFormField.EMERGENCY_PROTOCOL]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS,
        value: formData[SupervisoryReviewFormField.HYGIENE_PRECAUTIONS]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES,
        value: formData[SupervisoryReviewFormField.REPORTING_CHANGES]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT,
        value: formData[SupervisoryReviewFormField.GRIEVANCE_COMPLAINT]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER,
        value: formData[SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS,
        value: formData[SupervisoryReviewFormField.PPE_ACCESS]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED,
        value: formData[SupervisoryReviewFormField.SUPERVISION_OBSERVED]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED,
        value: formData[SupervisoryReviewFormField.TASK_OBSERVED]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS,
        value: formData[SupervisoryReviewFormField.PATIENT_STATUS]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS,
        value: formData[SupervisoryReviewFormField.COMMENTS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC,
        value: formData[SupervisoryReviewFormField.CAREGIVER_EDUCATION_TOPIC]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED,
        value: formData[SupervisoryReviewFormField.EDUCATION_PROVIDED]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_INITIALS_AUTHORIZATION,
        value: formData[SupervisoryReviewFormField.INITIALS_AUTHORIZATION]
      }
    ]
  }
}
